import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Calendar, MapPin, Truck, CheckCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { logError } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const navigate = useNavigate();
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState('inactive');
  const [planType, setPlanType] = useState('lets-be-pals');
  const [subscriptionUsage, setSubscriptionUsage] = useState(null);
  const [returns, setReturns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('returns');
  const [supportMessage, setSupportMessage] = useState('');
  const [supportSubject, setSupportSubject] = useState('');
  const [supportError, setSupportError] = useState('');
  const [selectedBooking, setSelectedBooking] = useState('');
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const [supportSuccess, setSupportSuccess] = useState(false);

  // Define loadReturns BEFORE useEffect that uses it
  const loadReturns = useCallback(async (email) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/bookings?email=${encodeURIComponent(email)}`);
      
      if (response.ok) {
        const data = await response.json();
        setReturns(data || []);
      } else {
        logError('Failed to load bookings');
        setReturns([]);
      }
    } catch (error) {
      logError('Error loading bookings:', error);
      setReturns([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadSubscriptionUsage = useCallback(async (email) => {
    try {
      const res = await fetch(`${API_URL}/api/users/${encodeURIComponent(email)}/subscription-usage`);
      if (!res.ok) return;
      const data = await res.json();
      setSubscriptionUsage(data);
      setPlanType(data.plan_type || 'lets-be-pals');
      // Refresh the locally-cached subscription state in case it was stale.
      setSubscriptionStatus(data.subscription_status || 'inactive');
    } catch (error) {
      logError('Failed to load subscription usage:', error);
    }
  }, []);

  useEffect(() => {
    const customerInfo = JSON.parse(sessionStorage.getItem('customerInfo') || '{}');
    if (customerInfo.email) {
      setCustomerEmail(customerInfo.email);
      setCustomerName(customerInfo.name || '');
      setSubscriptionStatus(customerInfo.isSubscription ? 'active' : 'inactive');
      loadReturns(customerInfo.email);
      loadSubscriptionUsage(customerInfo.email);
    } else {
      navigate('/login');
    }
  }, [navigate, loadReturns, loadSubscriptionUsage]);

  const getStatusBadge = (status) => {
    const baseClass = 'status-badge';
    switch (status) {
      case 'pending': return `${baseClass} bg-yellow-100 text-yellow-800`;
      case 'confirmed': return `${baseClass} bg-green-100 text-green-800`;
      case 'picked_up': return `${baseClass} bg-blue-100 text-blue-800`;
      case 'in_transit': return `${baseClass} bg-purple-100 text-purple-800`;
      case 'delivered': return `${baseClass} bg-gray-100 text-gray-800`;
      default: return baseClass;
    }
  };

  const handleSupportSubmit = async (e) => {
    e.preventDefault();
    setSupportSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/support/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: customerName,
          email: customerEmail,
          subject: supportSubject,
          message: supportMessage,
          booking_reference: selectedBooking
        })
      });

      if (response.ok) {
        setSupportSuccess(true);
        setSupportMessage('');
        setSupportSubject('');
        setSelectedBooking('');
        setTimeout(() => setSupportSuccess(false), 5000);
      }
    } catch (error) {
      logError('Failed to submit support request:', error);
      setSupportError('Failed to submit support request. Please try again.');
      // Error already shown to user via form validation
    }
    setSupportSubmitting(false);
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <nav className="nav-header">
        <div className="nav-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img 
            src="https://customer-assets.emergentagent.com/job_returnpal-pickup/artifacts/x8ux114u_Screenshot%202026-04-27%20165324.png" 
            alt="ReturnPal" 
            style={{ height: "50px", width: "auto" }}
          />
        </div>
        <div className="nav-cta">
          <Button onClick={() => navigate('/schedule-pickup')} className="btn-primary">
            Schedule New Pickup
          </Button>
        </div>
      </nav>

      <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto', paddingTop: 'calc(3.5rem + 2rem)' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>My Returns Dashboard</h1>
          {customerEmail && (
            <p style={{ color: '#6b7280' }}>Welcome back, {customerName || customerEmail}!</p>
          )}
        </div>

        {/* Primary CTA Banner — different copy for subscribers vs pay-as-you-go */}
        {subscriptionStatus === 'active' ? (
          <div
            data-testid="dashboard-cta-subscriber"
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderRadius: '12px',
              padding: '1.5rem 2rem',
              marginBottom: '2rem',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem',
              color: 'white',
            }}
          >
            <div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>
                Schedule your next pickup
              </h3>
              <p style={{ margin: '0.25rem 0 0', opacity: 0.95, fontSize: '0.95rem' }}>
                Your subscription includes
                {subscriptionUsage ? ` ${subscriptionUsage.pickups_remaining} pickup${subscriptionUsage.pickups_remaining === 1 ? '' : 's'} remaining this period.` : ' more pickups this period.'}
              </p>
            </div>
            <Button
              onClick={() => navigate('/schedule-pickup')}
              data-testid="dashboard-book-pickup-btn"
              style={{ backgroundColor: 'white', color: '#059669', fontWeight: 600 }}
            >
              Book a Pickup
            </Button>
          </div>
        ) : (
          <div
            data-testid="dashboard-cta-non-subscriber"
            style={{
              backgroundColor: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: '12px',
              padding: '1.5rem 2rem',
              marginBottom: '2rem',
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem',
            }}
          >
            <div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0, color: '#78350f' }}>
                Ready to send a return?
              </h3>
              <p style={{ margin: '0.25rem 0 0', color: '#92400e', fontSize: '0.95rem' }}>
                Book a one-off pickup with <strong>Let's Be Pals ($19.99)</strong> — or upgrade to a subscription for recurring savings.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <Button
                onClick={() => navigate('/schedule-pickup?plan=lets-be-pals')}
                data-testid="dashboard-book-pickup-btn"
                className="btn-primary"
              >
                Book a Pickup
              </Button>
              <Button
                onClick={() => navigate('/signup?upgrade=1')}
                variant="outline"
                data-testid="dashboard-upgrade-btn"
                style={{ borderColor: '#10b981', color: '#10b981' }}
              >
                See subscription plans
              </Button>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: '1rem', 
          marginBottom: '2rem'
        }}>
          <Card style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
            <CardContent style={{ padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ fontSize: '0.875rem', color: '#1e40af', fontWeight: '600' }}>Total Returns</p>
                  <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e3a8a' }}>{returns.length}</p>
                </div>
                <Package size={40} color="#3b82f6" />
              </div>
            </CardContent>
          </Card>

          <Card style={{ 
            backgroundColor: subscriptionStatus === 'active' ? '#f0fdf4' : '#fef3c7', 
            border: subscriptionStatus === 'active' ? '1px solid #bbf7d0' : '1px solid #fde68a' 
          }} data-testid="subscription-card">
            <CardContent style={{ padding: '1.5rem' }}>
              {subscriptionStatus === 'active' && subscriptionUsage ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <p style={{ fontSize: '0.875rem', color: '#15803d', fontWeight: '600', margin: 0 }}>
                      {(planType || 'Subscription').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </p>
                    <CheckCircle size={28} color="#22c55e" />
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#166534', display: 'grid', gap: '0.35rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Pickups remaining</span>
                      <strong data-testid="usage-pickups-remaining">{subscriptionUsage.pickups_remaining}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Packages remaining</span>
                      <strong data-testid="usage-packages-remaining">{subscriptionUsage.packages_remaining}</strong>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <p style={{ fontSize: '0.875rem', color: '#92400e', fontWeight: '600' }}>Subscription</p>
                    <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#78350f' }}>
                      Pay-Per-Use
                    </p>
                  </div>
                  <CheckCircle size={40} color="#f59e0b" />
                </div>
              )}
            </CardContent>
          </Card>

          <Card style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
            <CardContent style={{ padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ fontSize: '0.875rem', color: '#991b1b', fontWeight: '600' }}>Active Returns</p>
                  <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#7f1d1d' }}>
                    {returns.filter(r => r.status !== 'delivered').length}
                  </p>
                </div>
                <Truck size={40} color="#ef4444" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tab Navigation */}
        <div style={{ 
          display: 'flex', 
          gap: '0.5rem', 
          borderBottom: '2px solid #e5e7eb',
          marginBottom: '2rem'
        }}>
          <button
            onClick={() => setActiveTab('returns')}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              fontWeight: '600',
              color: activeTab === 'returns' ? '#10b981' : '#6b7280',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'returns' ? '3px solid #10b981' : 'none',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            📦 My Returns
          </button>
          <button
            onClick={() => setActiveTab('support')}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              fontWeight: '600',
              color: activeTab === 'support' ? '#10b981' : '#6b7280',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'support' ? '3px solid #10b981' : 'none',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            💬 Support
          </button>
        </div>

        {/* Returns Tab */}
        {activeTab === 'returns' && (
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>My Returns</h2>

          {isLoading ? (
            <p style={{ textAlign: 'center', padding: '2rem' }}>Loading your returns...</p>
          ) : returns.length === 0 ? (
            <Card>
              <CardContent style={{ padding: '3rem', textAlign: 'center' }}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_returnpal-pickup/artifacts/x8ux114u_Screenshot%202026-04-27%20165324.png"
                  alt="ReturnPal"
                  style={{ width: '120px', height: 'auto', margin: '0 auto 1.5rem', opacity: 0.7 }}
                />
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#4b5563', marginBottom: '0.5rem' }}>
                  No returns yet
                </h3>
                <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
                  Schedule your first pickup to get started!
                </p>
                <Button onClick={() => navigate('/schedule-pickup?plan=lets-be-pals')} data-testid="dashboard-schedule-first-btn" className="btn-primary">
                  Schedule First Pickup
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {returns.map((returnItem) => (
                <Card key={returnItem.confirmationNumber || returnItem._id} style={{ border: '1px solid #e5e7eb' }}>
                  <CardContent style={{ padding: '1.5rem' }}>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'auto 2fr auto auto auto auto',
                      gap: '1.5rem',
                      alignItems: 'center'
                    }}>
                      
                      {/* Status */}
                      <div>
                        <span className={getStatusBadge(returnItem.status)} style={{ 
                          padding: '0.25rem 0.75rem', 
                          borderRadius: '9999px', 
                          fontSize: '0.75rem', 
                          fontWeight: '600',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}>
                          {returnItem.status === 'pending' && <Clock size={12} />}
                          {returnItem.status === 'confirmed' && <CheckCircle size={12} />}
                          {returnItem.status}
                        </span>
                      </div>

                      {/* Confirmation & Location */}
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '1rem', marginBottom: '4px' }}>
                          #{returnItem.confirmationNumber}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <MapPin size={14} /> {returnItem.locationName}
                        </div>
                      </div>

                      {/* Pickup */}
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.875rem', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginBottom: '2px' }}>
                          <Calendar size={14} /> {returnItem.pickupDate}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {returnItem.pickupWindow}
                        </div>
                      </div>

                      {/* Packages */}
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginBottom: '2px', textTransform: 'uppercase' }}>Packages</div>
                        <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#059669' }}>
                          {returnItem.numberOfPackages}
                        </div>
                      </div>

                      {/* Carrier */}
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginBottom: '2px', textTransform: 'uppercase' }}>Carrier</div>
                        <div style={{ fontSize: '0.875rem', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                          <Truck size={14} /> {returnItem.carrier}
                        </div>
                      </div>

                      {/* Total */}
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginBottom: '2px', textTransform: 'uppercase' }}>Total</div>
                        <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1f2937' }}>
                          ${returnItem.totalCost?.toFixed(2) || '0.00'}
                        </div>
                      </div>

                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Support Tab */}
        {activeTab === 'support' && (
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>Contact Support</h2>
          
          {/* Your Bookings for Reference */}
          <Card style={{ marginBottom: '2rem' }}>
            <CardContent style={{ padding: '1.5rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>Your Bookings</h3>
              {returns.length > 0 ? (
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {returns.map((booking) => (
                    <div
                      key={booking.confirmationNumber || booking._id}
                      style={{
                        padding: '1rem',
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <div>
                          <p style={{ fontWeight: '600', color: '#10b981' }}>#{booking.confirmationNumber}</p>
                          <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                            📍 {booking.locationName} • 📅 {booking.pickupDate}
                          </p>
                          <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                            📦 {booking.numberOfPackages} package{booking.numberOfPackages > 1 ? 's' : ''}
                          </p>
                        </div>
                        <span className={getStatusBadge(booking.status)}>
                          {booking.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#6b7280' }}>No bookings yet</p>
              )}
            </CardContent>
          </Card>

          {/* Support Form */}
          <Card>
            <CardContent style={{ padding: '2rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1.5rem' }}>
                Need help? Send us a message
              </h3>
              
              {supportSuccess && (
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  color: '#166534'
                }}>
                  ✅ Message sent! We'll get back to you within 24 hours.
                </div>
              )}

              <form onSubmit={handleSupportSubmit}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Related Booking (Optional)
                  </label>
                  <select
                    value={selectedBooking}
                    onChange={(e) => setSelectedBooking(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="">Select a booking (optional)</option>
                    {returns.map((booking) => (
                      <option key={booking.confirmationNumber || booking._id} value={booking.confirmationNumber}>
                        #{booking.confirmationNumber} - {booking.locationName} ({booking.pickupDate})
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Subject *
                  </label>
                  <input
                    type="text"
                    required
                    value={supportSubject}
                    onChange={(e) => setSupportSubject(e.target.value)}
                    placeholder="Brief description of your issue"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Message *
                  </label>
                  <textarea
                    required
                    value={supportMessage}
                    onChange={(e) => setSupportMessage(e.target.value)}
                    placeholder="Please describe your issue or question in detail..."
                    rows="6"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={supportSubmitting}
                  className="btn-primary"
                  style={{ width: '100%' }}
                >
                  {supportSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>

              {/* Common FAQs */}
              <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e5e7eb' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Common Questions</h4>
                <div style={{ display: 'grid', gap: '0.75rem', fontSize: '0.875rem' }}>
                  <div>
                    <p style={{ fontWeight: '600', color: '#10b981' }}>When will my package be picked up?</p>
                    <p style={{ color: '#6b7280' }}>Pickups occur during your selected time window on your scheduled date.</p>
                  </div>
                  <div>
                    <p style={{ fontWeight: '600', color: '#10b981' }}>How do I track my return?</p>
                    <p style={{ color: '#6b7280' }}>Use the tracking number from your prepaid label on the carrier's website.</p>
                  </div>
                  <div>
                    <p style={{ fontWeight: '600', color: '#10b981' }}>Can I cancel or reschedule?</p>
                    <p style={{ color: '#6b7280' }}>Contact us at least 24 hours before your pickup time to make changes.</p>
                  </div>
                  <div>
                    <p style={{ fontWeight: '600', color: '#10b981' }}>What if I forgot to write the ID on my package?</p>
                    <p style={{ color: '#6b7280' }}>Contact us immediately with your booking number. We'll help identify your package.</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        )}

      </div>
    </div>
  );
};

export default Dashboard;
